import * as E from '../errors';
import UA     from './userAgent';


export class Canvas {
	readonly DOM: HTMLCanvasElement;
	readonly context: CanvasRenderingContext2D;

	async toBlob(mimeType: 'image/jepg' | 'image/png') {
		return await new Promise<Blob>((resolve, reject) => {
			this.DOM.toBlob((blob) => {
				if (blob) {
					resolve(blob);
				} else {
					reject(new Error('Cannot generate blob!'));
				}
			}, mimeType);
		});
	};

	constructor(width: number, height: number) {
		this.DOM = factory({
			tag: 'canvas',
			attributes: {
				width: width.toString(),
				height: height.toString()
			}
		}, HTMLCanvasElement);
		const context = this.DOM.getContext('2d');
		if (!context) {
			throw E.library('Cannot create 2D context!', {
				canvas: this
			});
		}
		this.context = context;
	};
};

export function create2DCanvas(width: number, height: number) {
	const DOMCanvas = factory({
		tag: 'canvas',
		attributes: {
			width: width.toString(),
			height: height.toString()
		}
	}, HTMLCanvasElement);
	const context = DOMCanvas.getContext('2d');
	if (!context) {
		throw E.library('Cannot create 2D context!', {
			DOMCanvas
		});
	}
	return {
		DOM: DOMCanvas,
		context
	};
};

export function parseName(name: string): {
	namespace: string | null;
	name: string;
} {
	const part = name.split('=');
	if (part.length === 1) {
		return {
			namespace: null,
			name
		};
	} else if (part.length === 2) {
		return {
			namespace: <string>part[1],
			name: <string>part[0]
		};
	}
	throw E.user(`parseTagName@name<${name}> is not a tag name!`, {name});
};

export interface IFactoryNodeDescriptor {
	tag: string;                   // default to div
	classList: string[];                 // default to []
	childs: (IFactoryNodeDescriptor | Element | string | null)[];  // default to []
	value?: string;
	style?: Partial<CSSStyleDeclaration>;
	cb?: (x: Node | Element) => void;
	text?: string; //textContent, cannot be specified in conjunction with html
	html?: string; //innerHTML, cannot be specified in conjunction with text
	events: Record<string, (...args: any[]) => any>;
	attributes: Record<string, string>;
	dataset: Record<string, string>;
	details: Record<string, unknown>;
};
export type IFactoryNodePartialDescriptor = Partial<
	Omit<IFactoryNodeDescriptor, 'childs'>
> & {
	childs?: (IFactoryNodePartialDescriptor | Element | string | null)[];
};
const factoryNormalizeNodeDescriptor = (
	descriptor: IFactoryNodePartialDescriptor
): IFactoryNodeDescriptor => {
	if (typeof descriptor === 'string') {
		return descriptor;
	}
	const childs = (
		(descriptor.childs || []).
		filter<Exclude<IFactoryNodeDescriptor['childs'][number], null>>((x): x is string | Element | IFactoryNodeDescriptor => !!x).
		map(x => {
			if (
				(typeof x === 'string') ||
				(x instanceof Node)
			) {
				return x;
			} else {
				return factoryNormalizeNodeDescriptor(x);
			}
		})
	);
	return {
		tag: descriptor.tag || 'div',
		classList: descriptor.classList || [],
		value: descriptor.value,
		text: descriptor.text,
		html: descriptor.html,
		style: descriptor.style,
		events: descriptor.events || {},
		attributes: descriptor.attributes || {},
		dataset: descriptor.dataset || {},
		details: descriptor.details || {},
		cb: descriptor.cb,
		childs
	};
};
const getTemporaryDiv = (() => {
	const divs: HTMLDivElement[] = [];
	const baseDiv = document.createElement('div');
	baseDiv.classList.add('temporary');
	const getTemporaryDiv = () => {
		if (divs.length !== 0) {
			return <HTMLDivElement>divs.shift();
		} else {
			divs.push(<HTMLDivElement>baseDiv.cloneNode());
			divs.push(<HTMLDivElement>baseDiv.cloneNode());
			divs.push(<HTMLDivElement>baseDiv.cloneNode());
			divs.push(<HTMLDivElement>baseDiv.cloneNode());
			return <HTMLDivElement>baseDiv.cloneNode();
		}
	};
	getTemporaryDiv.relase = (div: HTMLDivElement) => {
		divs.push(div);
	};
	return getTemporaryDiv;
})();

export function factory<
	TRootNodeType extends typeof HTMLElement
>(
	rootNodeDescriptor?: IFactoryNodePartialDescriptor,
	RootNodeType?: TRootNodeType
): InstanceType<TRootNodeType> {
	try {
		rootNodeDescriptor = rootNodeDescriptor || {};
		const file: [IFactoryNodeDescriptor, Node | null, HTMLDivElement?][] = [
			[factoryNormalizeNodeDescriptor(rootNodeDescriptor), null]
		];
		let root: InstanceType<TRootNodeType> | null = null;
		while (file.length !== 0) {
			const [nodeDescriptor, parent, toBeReplaced] = file.shift() as [
				IFactoryNodeDescriptor, Node | null, HTMLDivElement?
			];
			const tag = parseName(nodeDescriptor.tag || 'div');
			let node: Element;
			if (tag.namespace) {
				node = document.createElementNS(tag.namespace, tag.name);
			} else {
				node = document.createElement(tag.name);
			}
			if (!(node instanceof Element)) {
				throw new RangeError;
			}
			for (const _class of nodeDescriptor.classList) {
				node.classList.add(_class);
			}
			for (const [eventName, callback] of Object.entries(nodeDescriptor.events)) {
				if (typeof callback !== 'function') {
					throw E.user(
						`Event<${eventName}> callback<${callback}> is not a function!`,
						{
							nodeDescriptor,
							eventName,
							callback
						}
					);
				}
				if (eventName[0] === '_') {
					node.addEventListener(eventName.slice(1), callback);
				} else {
					node.addEventListener(eventName, callback);
				}
			}
			if (nodeDescriptor.value !== undefined) {
				if (!(node instanceof HTMLInputElement)) {
					throw E.user(
						'Value property can only be specified on a HTMLInputElement!',
						{nodeDescriptor}
					);
				}
				node.value = nodeDescriptor.value;
			}
			for (const [attrKey, attrValue] of Object.entries(nodeDescriptor.attributes)) {
				const k = parseName(attrKey);
				if (k.namespace) {
					node.setAttributeNS(k.namespace, k.name, attrValue);
				} else {
					node.setAttribute(k.name, attrValue);
				}
			}
			if (nodeDescriptor.style) {
				if (!(node instanceof HTMLElement)) {
					throw E.user(
						'Style property can only be specified on a HTMLElement!',
						{nodeDescriptor}
					);
				}
				Object.assign(node.style, nodeDescriptor.style);
			}
			if (nodeDescriptor.cb) {
				nodeDescriptor.cb(node);
			}
			if (nodeDescriptor.text && nodeDescriptor.html) {
				throw E.user(
					'Text and html properties cannot be specified in same time!',
					{nodeDescriptor}
				);
			}
			if (nodeDescriptor.text) {
				node.textContent = nodeDescriptor.text;
			} else if (nodeDescriptor.html) {
				node.innerHTML = nodeDescriptor.html;
			}
			const hasDOMChild = nodeDescriptor.childs.find(x =>
				(typeof x === 'string') ||
				(x instanceof Element)
			);
			for (const child of nodeDescriptor.childs) {
				if (!child) {
					continue;
				}
				if (child instanceof Element) {
					node.appendChild(child);
				} else if (typeof child === 'string') {
					node.appendChild(document.createTextNode(child));
				} else {
					if (hasDOMChild) {
						const tmpNode = getTemporaryDiv();
						node.appendChild(tmpNode);
						file.push([child, node, tmpNode]);
					} else {
						file.push([child, node]);
					}
				}
			}
			for (const [key, value] of Object.entries(nodeDescriptor.dataset)) {
				node.setAttribute('data-' + key, value);
			}
			;
			if (nodeDescriptor.details !== undefined) {
				(<any>node).details = nodeDescriptor.details;
			}
			if (parent !== null) {
				if (toBeReplaced) {
					toBeReplaced.replaceWith(node);
					getTemporaryDiv.relase(toBeReplaced);
				} else {
					parent.appendChild(node);
				}
			} else {
				if (RootNodeType && !(node instanceof <any>RootNodeType)) {
					throw E.user(
						(
							`Element final type<${node.constructor.name}> ` +
							`is not the expected<${RootNodeType.name}>!`
						),
						{
							nodeDescriptor,
							node,
							RootNodeType
						}
					);
				}
				root = <InstanceType<TRootNodeType>>node;
			}
		}
		if (!root) {
			throw new RangeError;
		}
		return root;
	} catch (error: any) {
		throw E.bindAsBound('DOMFactory : Cannot create the DOM!', {rootNodeDescriptor, RootNodeType}, error);
	}
};

export function formDataFromObject(obj: { [key: string]: any }) {
	const fd = new FormData;
	Object.entries(obj).forEach(function (kv) {
		if (
			(kv[1] instanceof Object) &&
			!(kv[1] instanceof FormData) &&
			!(kv[1] instanceof File)
		) {
			fd.append(kv[0], JSON.stringify(kv[1]));
		} else if (kv[1] === null) {
			fd.append(kv[0], '');
		} else {
			fd.append(kv[0], kv[1]);
		}
	});
	return fd;
};

export function clearInlineStyle(DOMNode: HTMLElement) {
	DOMNode.style.cssText = '';
};

/* Only linking STD-API.js is not sufficient for using this manager. */
interface IManagedDescriptor {
	DOM: HTMLElement;
	CSS?: CSSStyleDeclaration;
	side: 'all' | 'horizontal' | 'vertical' | 'top' | 'bottom' | 'left' | 'right';
};

class Managed {
	CSS: CSSStyleDeclaration;
	sides = {
		top: {
			enabled: false,
			DOM: <HTMLElement | null>null
		},
		bottom: {
			enabled: false,
			DOM: <HTMLElement | null>null
		},
		left: {
			enabled: false,
			DOM: <HTMLElement | null>null
		},
		right: {
			enabled: false,
			DOM: <HTMLElement | null>null
		}
	};

	constructor(desc: IManagedDescriptor) {
		this.CSS = desc.CSS || <CSSStyleDeclaration>{};
		const DOMOverflowsMaskManager = document.getElementById('overflowsMaskManager');
		if (!DOMOverflowsMaskManager) {
			throw new RangeError;
		}
		switch (desc.side) {
			case 'all': {
				this.sides.top.DOM = <HTMLElement>factory({});
				this.sides.bottom.DOM = <HTMLElement>factory({});
				this.sides.left.DOM = <HTMLElement>factory({});
				this.sides.right.DOM = <HTMLElement>factory({});
				DOMOverflowsMaskManager.appendChild(this.sides.top.DOM);
				DOMOverflowsMaskManager.appendChild(this.sides.bottom.DOM);
				DOMOverflowsMaskManager.appendChild(this.sides.left.DOM);
				DOMOverflowsMaskManager.appendChild(this.sides.right.DOM);
			}
				break;
			case 'horizontal': {
				this.sides.left.DOM = <HTMLElement>factory({});
				this.sides.right.DOM = <HTMLElement>factory({});
				DOMOverflowsMaskManager.appendChild(this.sides.left.DOM);
				DOMOverflowsMaskManager.appendChild(this.sides.right.DOM);
			}
				break;
			case 'vertical': {
				this.sides.top.DOM = <HTMLElement>factory({});
				this.sides.bottom.DOM = <HTMLElement>factory({});
				DOMOverflowsMaskManager.appendChild(this.sides.top.DOM);
				DOMOverflowsMaskManager.appendChild(this.sides.bottom.DOM);
			}
				break;
			case 'top': {
				this.sides.top.DOM = <HTMLElement>factory({});
				DOMOverflowsMaskManager.appendChild(this.sides.top.DOM);
			}
				break;
			case 'bottom': {
				this.sides.bottom.DOM = <HTMLElement>factory({});
				DOMOverflowsMaskManager.appendChild(this.sides.bottom.DOM);
			}
				break;
			case 'left': {
				this.sides.left.DOM = <HTMLElement>factory({});
				DOMOverflowsMaskManager.appendChild(this.sides.left.DOM);
			}
				break;
			case 'right': {
				this.sides.right.DOM = <HTMLElement>factory({});
				DOMOverflowsMaskManager.appendChild(this.sides.right.DOM);
			}
				break;
		}
	};
};

interface IOverflowsMaskManagerEnableDescriptor extends IManagedDescriptor {
	box: {
		width?: string;
		height?: string;
	};
};

class OverflowsMaskManager {
	manageds = <Map<HTMLElement, Managed>>new Map;

	declare(desc: IManagedDescriptor) {
		const data = new Managed(desc);
		this.manageds.set(desc.DOM, data);
		return ({
			data: data,
			DOM: desc.DOM
		});
	};

	/*
		DOM;
		CSS;
		side : 'all' | 'horizontal' | 'vertical' | 'top' | 'bottom' | 'left' | 'right';
		box : {
			width  : CSSLength;
			height : CSSLength;
		};
	*/
	enable(desc: IOverflowsMaskManagerEnableDescriptor) {
		const data = this.manageds.get(desc.DOM);
		if (!data) {
			throw new RangeError;
		}
		let doTop = false;
		let doBottom = false;
		let doLeft = false;
		let doRight = false;
		const h = desc.DOM.offsetHeight;
		const w = desc.DOM.offsetWidth;
		switch (desc.side) {
			case 'all': {
				doTop = true;
				doBottom = true;
				doLeft = true;
				doRight = true;
			}
				break;
			case 'horizontal': {
				doLeft = true;
				doRight = true;
			}
				break;
			case 'vertical': {
				doTop = true;
				doBottom = true;
			}
				break;
			case 'top': {
				doTop = true;
			}
				break;
			case 'bottom': {
				doBottom = true;
			}
				break;
			case 'left': {
				doLeft = true;
			}
				break;
			case 'right': {
				doRight = true;
			}
				break;
		}
		const sl = data.sides.left;
		const sr = data.sides.right;
		const st = data.sides.top;
		const sb = data.sides.bottom;
		if (sl.DOM) {
			clearInlineStyle(sl.DOM);
		}
		if (sr.DOM) {
			clearInlineStyle(sr.DOM);
		}
		if (st.DOM) {
			clearInlineStyle(st.DOM);
		}
		if (sb.DOM) {
			clearInlineStyle(sb.DOM);
		}
		const cr = desc.DOM.getBoundingClientRect();
		let boxW = null;
		let boxH = null;
		if (desc.box.height) {
			const n = parseInt(desc.box.height);
			const unit = desc.box.height.slice(n.toString().length);
			if (unit === 'px') {
				boxH = n;
			} else if (unit === '%') {
				boxH = h - ((h / 100) * n);
			}
		}
		if (desc.box.width) {
			const n = parseInt(desc.box.width);
			const unit = desc.box.width.slice(n.toString().length);
			if (unit === 'px') {
				boxW = n;
			} else if (unit === '%') {
				boxW = w - ((w / 100) * n);
			}
		}
		if (doTop) {
			st.enabled = true;
			const style = Object.assign(
				{},
				(data.CSS || {}).top,
				(desc.CSS || {}).top
			);
			if (!st.DOM) {
				throw new RangeError;
			}
			Object.assign(st.DOM.style, {
				height: boxH + 'px',
				width: w + 'px',
				top: cr.top + 'px',
				left: cr.left + 'px'
			}, style);
		} else if (st.enabled) {
			st.enabled = false;
			if (st.DOM) {
				st.DOM.style.display = 'none';
			}
		}
		if (doBottom) {
			sb.enabled = true;
			const style = Object.assign(
				{},
				(data.CSS || {}).bottom,
				(desc.CSS || {}).bottom
			);
			if (!sb.DOM || !boxH) {
				throw new RangeError;
			}
			Object.assign(sb.DOM.style, {
				height: boxH + 'px',
				width: w + 'px',
				top: cr.bottom - boxH + 'px',
				left: cr.left + 'px'
			}, style);
		} else if (sb.enabled) {
			sb.enabled = false;
			if (sb.DOM) {
				sb.DOM.style.display = 'none';
			}
		}
		if (doLeft) {
			sl.enabled = true;
			var style = Object.assign(
				{},
				(data.CSS || {}).left,
				(desc.CSS || {}).left
			);
			if (!sl.DOM || !boxH) {
				throw new RangeError;
			}
			Object.assign(sl.DOM.style, {
				height: h + 'px',
				width: boxW + 'px',
				top: cr.top + 'px',
				left: cr.left + 'px'
			}, style);
		} else if (sl.enabled) {
			sl.enabled = false;
			if (sl.DOM) {
				sl.DOM.style.display = 'none';
			}
		}
		if (doRight) {
			sr.enabled = true;
			const style = Object.assign(
				{},
				(data.CSS || {}).right,
				(desc.CSS || {}).right
			);
			if (!sr.DOM || !boxW) {
				throw new RangeError;
			}
			Object.assign(sr.DOM.style, {
				height: h + 'px',
				width: boxW + 'px',
				top: cr.top + 'px',
				left: cr.right - boxW + 'px'
			}, style);
		} else if (sr.enabled) {
			sr.enabled = false;
			if (sr.DOM) {
				sr.DOM.style.display = 'none';
			}
		}
	};

	/*
		DOM;
	*/
	disable(DOM: HTMLElement) {
		var data = this.manageds.get(DOM);
		if (!data) {
			throw new RangeError;
		}
		var sl = data.sides.left;
		var sr = data.sides.right;
		var st = data.sides.top;
		var sb = data.sides.bottom;
		if (sl.DOM) {
			clearInlineStyle(sl.DOM);
			sl.enabled = false;
		}
		if (sr.DOM) {
			clearInlineStyle(sr.DOM);
			sr.enabled = false;
		}
		if (st.DOM) {
			clearInlineStyle(st.DOM);
			st.enabled = false;
		}
		if (sb.DOM) {
			clearInlineStyle(sb.DOM);
			st.enabled = false;
		}
	};

	disableInvisible() {
		for (const DOM of this.manageds.keys()) {
			if (!isVisible(DOM)) {
				this.disable(DOM);
			}
		}
	};

	getManageds() {
		return (this.manageds);
	};

	constructor() {
	};
};
export const overflowsMaskManager = (() => {
	return (new OverflowsMaskManager);
})();

class CursorActivation {
	isActived = true;
	ids = new Set;
	private checkIsDisabledIntervalId: number | null = null;

	private checkIsDisabled() {
		if (document.body.style.pointerEvents !== 'none') {
			document.body.style.pointerEvents = 'none';
		}
	};

	enable(id: string) {
		this.ids.delete(id);
		if (this.ids.size === 0) {
			this.isActived = true;
			clearInterval(this.checkIsDisabledIntervalId || undefined);
			document.body.style.pointerEvents = '';
			document.body.classList.remove('cursorActivationDisabled');
		}
	};

	disable(id: string) {
		this.ids.add(id);
		if (this.isActived) {
			this.isActived = false;
			document.body.style.pointerEvents = 'none';
			document.body.classList.add('cursorActivationDisabled');
			this.checkIsDisabledIntervalId = (
				setInterval(this.checkIsDisabled, 5) as any as number
			);
		}
	};

	constructor() {
		this.checkIsDisabled = this.checkIsDisabled.bind(this);
	};
};
export const cursorActivation = new CursorActivation;

export function isVisible(DOM: HTMLElement) {
	if ((!DOM.offsetWidth && !DOM.offsetHeight) || !DOM.getClientRects().length) {
		return (false);
	}
	const rect = DOM.getBoundingClientRect();
	if (
		(rect.bottom < 0) ||
		(rect.top > window.innerHeight) ||
		(rect.right < 0) ||
		(rect.left > window.innerWidth)
	) {
		return (false);
	}
	const cs = getComputedStyle(DOM);
	if (cs.visibility === 'hidden') {
		return (false);
	}
	return (true);
};

export class VisitorController {
	readonly rootElement: Element;
	isStopped = false;

	stop() {
		this.isStopped = true;
	};

	computeSelectorFromRoot(leafElement: Element) {
		let element: Element = leafElement;
		const indexes: number[] = [];
		while (element !== this.rootElement) {
			if (!element.parentElement) {
				throw new Error('');
			}
			indexes.unshift(Array.from(element.parentElement.children).indexOf(element));
			element = element.parentElement;
		}
		if (indexes.length === 0) {
			return (':scope');
		}
		return (`:scope > ${indexes.map(x => `:nth-child(${x + 1})`).join(' > ')}`);
	};

	constructor(rootElement: Element) {
		this.rootElement = rootElement;
	};
};

export async function visit(
	rootElement: Element,
	cb: (element: Element, controller: VisitorController) => Promise<void>,
	controller: VisitorController = new VisitorController(rootElement)
) {
	const stack = [rootElement];
	while (stack.length !== 0) {
		const element = <Element>stack.shift();
		await cb(element, controller);
		if (controller.isStopped) {
			break;
		}
		for (const c of Array.from(element.children)) {
			stack.push(c);
		}
	}
};

export function visitSync(
	rootElement: Element,
	cb: (element: Element, controller: VisitorController) => void,
	controller: VisitorController = new VisitorController(rootElement)
) {
	const stack = [rootElement];
	while (stack.length !== 0) {
		const element = <Element>stack.shift();
		cb(element, controller);
		if (controller.isStopped) {
			break;
		}
		for (const c of Array.from(element.children)) {
			stack.push(c);
		}
	}
};

export class VisitorAccumulatorController<TAccumulator> extends VisitorController {
	accumulator: TAccumulator;

	updateAccumulator(accumulator: TAccumulator) {
		this.accumulator = accumulator;
	};

	constructor(rootElement: Element, accumulator: TAccumulator) {
		super(rootElement);
		this.accumulator = accumulator;
	};
};

export async function visitAccumulator<TAccumulator>(
	rootElement: Element,
	accumulator: TAccumulator,
	cb: (element: Element, controller: VisitorAccumulatorController<TAccumulator>) => Promise<void>,
	controller: VisitorAccumulatorController<TAccumulator> = new VisitorAccumulatorController(rootElement, accumulator)
) {
	await visit(rootElement, async (element, _controller) => {
		await cb(element, controller);
	});
	return (accumulator);
};

export function visitAccumulatorSync<TAccumulator>(
	rootElement: Element,
	accumulator: TAccumulator,
	cb: (element: Element, controller: VisitorAccumulatorController<TAccumulator>) => void,
	controller: VisitorAccumulatorController<TAccumulator> = new VisitorAccumulatorController(rootElement, accumulator)
) {
	visitSync(rootElement, (element, _controller) => {
		cb(element, controller);
	});
	return (accumulator);
};

/**
 * Return if object is a window.
 * Useful when the window object is not top,
 * Firefox is ok with instanceof but chrome return false so check constructor
 */
export function isWindow(x: any) {
	try {
		if (!x) {
			return (false);
		} else if (
			//Firefox or w === top
			(x instanceof Window) ||
			//chrome if w !== top
			(
				x.constructor &&
				x.constructor.name &&
				(x.constructor.name === Window.name) &&
				x.constructor.toString().includes('[native code')
			)
		) {
			return (true);
		}
	} catch (e: any) {
		if (e && e.message && e.message.includes('cross-origin')) {
			return (
				(x === globalThis.top) ||
				((x === x.window) && x.top && x.postMessage)
			);
		}
	}
	return (false);
};

export interface IOpenFilesSelectorDescriptor {
	accept?: string; // format : "mimetype1, mimetype2, ..., mimetypeN"
};
// @ts-ignore
export const filesSelector = new class FilesSelector {
	private inputDOM: HTMLInputElement;
	private focusDispatched = false;
	private isOpen = false;
	private ppack = <{
		promise: Promise<null | File[]>;
		resolve: (result: null | File[]) => void;
		reject: (error: any) => void;
	} | null>null;
	private intervalId: ReturnType<typeof setInterval> | null = null;

	initialize(inputContainerDOM: HTMLDivElement | HTMLBodyElement) {
		document.body.addEventListener('focus', this.onBodyFocus.bind(this), true);
		inputContainerDOM.appendChild(this.inputDOM);
	};

	open(desc: IOpenFilesSelectorDescriptor): Promise<null | File[]> {
		// /* iOS never dispatch focus event so we can't know if it has been closed... */
		// if (API.navigator.OS.isIos === true) {
		// 	isOpen = false;
		// }
		if (this.isOpen === true) {
			return (Promise.resolve(null));
		}
		const p = new Promise<File[] | null>((resolve, reject) => {
			this.ppack = {
				promise: null as any as Promise<any>,
				resolve,
				reject
			};
		});
		if (!this.ppack) {
			throw E.library('The promise implementation is broken!', {});
		}
		this.isOpen = true;
		this.ppack.promise = p;
		this.inputDOM.value = '';
		this.inputDOM.setAttribute('accept', desc.accept || '');
		if (document.activeElement !== this.inputDOM) {
			this.inputDOM.focus();
		}
		this.inputDOM.click();
		this.intervalId = setInterval(this.onTick.bind(this), 25);
		requestAnimationFrame(() => {
			this.focusDispatched = true;
		});
		return (p);
	};


	private terminate(value: null | File[]) {
		if (!this.ppack) {
			throw E.library('', {});
		}
		const resolve = this.ppack.resolve;
		this.ppack = null;
		this.isOpen = false;
		this.focusDispatched = false;
		if (this.intervalId) {
			clearInterval(this.intervalId);
			this.intervalId = null;
		}
		resolve(value);
	};

	private onBodyFocus() {
		if (
			!this.focusDispatched ||
			!this.isOpen ||
			(document.activeElement === this.inputDOM)
		) {
			return;
		}
		this.terminate(null);
	};

	private onBlur() {
		if (
			!this.focusDispatched ||
			!this.isOpen ||
			(document.activeElement === this.inputDOM)
		) {
			return;
		}
		this.terminate(null);
	};

	private onChange() {
		if (!this.isOpen) {
			return;
		}
		const v = this.inputDOM.files ? Array.from(this.inputDOM.files) : null;
		this.terminate(v);
	};


	private onTick() {
		if (this.isOpen && (document.activeElement !== this.inputDOM)) {
			this.terminate(null);
		}
	};

	private onClick(ev: Event) {
		ev.stopPropagation();
	};

	constructor() {
		this.inputDOM = factory<typeof HTMLInputElement>({
			tag: 'input',
			attributes: {
				type: 'file'
			},
			style: {
				position: 'absolute',
				width: '5px',
				height: '5px',
				left: '-80px',
				top: '-80px',
				opacity: '0.5'
			},
			events: {
				blur: this.onBlur.bind(this),
				change: this.onChange.bind(this),
				input: this.onChange.bind(this),
				click: this.onClick.bind(this)
			}
		});
	};
};
export const openFilesSelector = Object.assign(
	filesSelector.open.bind(filesSelector),
	{
		initialize(inputContainerDOM: HTMLDivElement | HTMLBodyElement) {
			filesSelector.initialize(inputContainerDOM);
		}
	}
);

export const openFilesSelectorWithFilesTypes = (accept: string) => {
	return Object.assign(
		filesSelector.open.bind(filesSelector, {accept}),
		{
			initialize(inputContainerDOM: HTMLDivElement | HTMLBodyElement) {
				filesSelector.initialize(inputContainerDOM);
			}
		}
	);
};

// export const openFilesSelectorWithFilesTypes = Object.assign(
// 	filesSelector.open.bind(filesSelector, {}),
// 	{
// 		initialize(inputContainerDOM : HTMLDivElement | HTMLBodyElement) {
// 			filesSelector.initialize(inputContainerDOM);
// 		}
// 	}
// );

export function fileRead(
	input: File | Blob,
	outputMethod: 'ArrayBuffer'
): Promise<{
	input: File | Blob;
	output: ArrayBuffer;
}>;
export function fileRead(
	input: File | Blob,
	outputMethod: 'DataURL' | 'Text'
): Promise<{
	input: File | Blob;
	output: string;
}>;
export async function fileRead(
	input: File | Blob,
	outputMethod: 'ArrayBuffer' | 'DataURL' | 'Text'
): Promise<{
	input: File | Blob;
	output: unknown;
}> {
	return (await new Promise((res, rej) => {
		const reader = Object.assign(new FileReader, {
			onerror: rej,
			onabort: rej,
			onload(ev: ProgressEvent) {
				res({
					input: input,
					output: (<any>ev).target.result
				});
			}
		});
		const methodName = 'readAs' + outputMethod;
		if (methodName in reader) {
			(<any>reader)[methodName](input);
		} else {
			throw E.library('', {});
		}
	}));
};

export function arrayBufferToB64(buffer: ArrayBuffer, type: string) {
	let binary = '';
	const bytes = new Uint8Array(buffer);
	for (const byte of bytes) {
		binary += String.fromCharCode(byte);
	}
	const b64 = window.btoa(binary);
	return ('data:' + type + ';base64,' + b64);
};

export async function createImageFrom(
	source: File | Blob | string
): Promise<HTMLImageElement> {
	return (await new Promise(async (res, rej) => {
		const b64 = (
			(typeof source === 'string') ? source :
				arrayBufferToB64(
					(await fileRead(source, 'ArrayBuffer')).output,
					source.type
				)
		);
		const image = factory({
			tag: 'img',
			attributes: {
				src: b64
			},
			events: {
				load: () => res(image),
				error: rej
			}
		}) as HTMLImageElement;
	}));
};

export function qs(from: HTMLElement, pattern: string): HTMLElement {
	const match = from.querySelector(pattern);
	if (!match) {
		throw E.user(
			`Pattern<${pattern}> do not match anything`,
			{
				from,
				pattern
			}
		);
	}
	if (!(match instanceof HTMLElement)) {
		throw E.user(
			`Pattern<${pattern}> do not , an HTMLElement`,
			{
				from,
				pattern,
				match
			}
		);
	}
	return (match);
};

export function getOffset(element: HTMLElement) {
	const offset = {
		left: element.offsetLeft,
		top: element.offsetTop
	};
	while (element.offsetParent) {
		element = <HTMLElement>element.offsetParent;
		offset.left += element.offsetLeft;
		offset.top += element.offsetTop;
	}
	return (offset);
};

export function getNonNativesGlobalsList(
	context: Window | WindowProxy | null = window
) {
	const sub = window.open();
	if (sub === null) {
		throw E.library('getNonNativesGlobalsList : Cannot open child window!', {});
	}
	const keysSub = Object.getOwnPropertyNames(sub.window);
	const keys = Object.getOwnPropertyNames(context);
	const diff = keys.filter(x => !keysSub.includes(x));
	sub.close();
	return (diff);
};
export const parse = (DOMParser => (
	(...x: Parameters<DOMParser['parseFromString']>) => DOMParser.parseFromString(...x)
))(new DOMParser);

export function removeChilds(node: Element) {
	if (!node) {
		return;
	}
	while (node.childNodes.length !== 0) {
		(<any>node.childNodes[0]).remove();
	}
	return (node);
};

export function sortChildsByLine(DOMContainer: HTMLElement) {
	const lines: Record<number, HTMLElement[]> = {};
	for (const child of <HTMLElement[]>Array.from(DOMContainer.children)) {
		const ot = child.offsetTop;
		if (lines[ot] === undefined) {
			lines[ot] = [];
		}
		(<any>lines[ot]).push(child);
	}
	return (Object.values(lines));
};

export function isWrapped(DOMContainer: HTMLElement) {
	var previous: HTMLElement | null = null;
	for (const child of <HTMLElement[]>Array.from(DOMContainer.children)) {
		if (previous && (previous.offsetTop < child.offsetTop)) {
			return (true);
		}
		previous = child;
	}
	return (false);
};

export function getRealWidth(DOMElement: HTMLElement) {
	var cs = getComputedStyle(DOMElement);
	var w = DOMElement.offsetWidth;
	if (cs.marginLeft !== '') {
		w += parseInt(cs.marginLeft);
	}
	if (cs.marginRight !== '') {
		w += parseInt(cs.marginRight);
	}
	return (w);
};

export function desannotateFlexItems(DOMContainer: HTMLElement) {
	for (const child of <HTMLElement[]>Array.from(DOMContainer.children)) {
		child.classList.remove('flex-line-first-child');
		child.classList.remove('flex-line-last-child');
		child.classList.remove('flex-line-first');
		child.classList.remove('flex-line-last');
		child.style.marginRight = '';
		child.style.breakAfter = '';
	}
	;
};

export function annotateFlexItems(DOMContainer: HTMLElement) {
	if (isWrapped(DOMContainer)) {
		(<HTMLElement[]>Array.from(DOMContainer.children)).forEach(function (child) {
			child.classList.remove('flex-line-first-child');
			child.classList.remove('flex-line-last-child');
			child.classList.remove('flex-line-first');
			child.classList.remove('flex-line-last');
			child.style.breakAfter = '';
		});
		var lines = sortChildsByLine(DOMContainer);
		var lineLastIdx = lines.length - 1;
		lines.forEach(function (childs, lineIdx) {
			var childLastIdx = childs.length - 1;
			childs.forEach(function (child, childIdx) {
				if ((childIdx === 0) && (childIdx === childLastIdx)) {
					child.classList.add('flex-line-first-child');
					child.classList.add('flex-line-last-child');

					if (UA.getBrowser().name?.toLowerCase().includes('firefox')) {
						child.style.breakAfter = 'always';
					} else {
						// var occupedSize = childs.map(function(x) {
						// 	return (getRealWidth(x));
						// }).reduce(function(a, c) {
						// 	return (a + c);
						// }, 0);
					}
				} else if (childIdx === 0) {
					child.classList.add('flex-line-first-child');
				} else if (childIdx === childLastIdx) {
					child.classList.add('flex-line-last-child');
					if (UA.getBrowser().name?.toLowerCase().includes('firefox')) {
						child.style.breakAfter = 'always';
					} else {
						// var occupedSize = childs.map(function(x) {
						// 	return (getRealWidth(x));
						// }).reduce(function(a, c) {
						// 	return (a + c);
						// }, 0);
					}
				}
				if (lineIdx === 0) {
					child.classList.add('flex-line-first');
				} else if (lineIdx === lineLastIdx) {
					child.classList.add('flex-line-last');
				}
			});
		});
	} else {
		var lastIdx = DOMContainer.children.length - 1;
		(<HTMLElement[]>Array.from(DOMContainer.children)).forEach(function (child, i) {
			if ((i === 0) && (i === lastIdx)) {
				child.classList.add('flex-line-first-child');
				child.classList.add('flex-line-last-child');
				child.style.breakAfter = 'always';
			} else if (i === 0) {
				child.classList.add('flex-line-first-child');
				child.classList.remove('flex-line-last-child');
				child.style.breakAfter = '';
			} else if (i === lastIdx) {
				child.classList.remove('flex-line-first-child');
				child.classList.add('flex-line-last-child');
				child.style.breakAfter = 'always';
			} else {
				child.classList.remove('flex-line-first-child');
				child.classList.remove('flex-line-last-child');
				child.style.breakAfter = '';
			}
			child.classList.add('flex-line-first');
			child.classList.add('flex-line-last');
			child.style.marginRight = '';
		});
	}
};

export function createCSSLink(href: string) {
	return (factory({
		tag: 'link',
		attributes: {
			rel: 'stylesheet',
			type: 'text/css',
			href
		}
	}));
};

export function removeAllChild(element: HTMLElement) {
	while (element.children.length) {
		(<HTMLElement>element.children[0]).remove();
	}
};

export async function awaitDocumentIsInteractive() {
	if (document.readyState !== 'loading') {
		return;
	}
	await new Promise<void>((resolve, reject) => {
		try {
			let alreadyInProcessing = false;
			document.addEventListener('readystatechange', () => {
				if (alreadyInProcessing) {
					return;
				}
				if (document.readyState !== 'loading') {
					alreadyInProcessing = true;
					resolve();
				}
			});
		} catch (error: any) {
			reject(error);
		}
	});
};

export async function awaitDocumentIsLoaded() {
	if (document.readyState === 'complete') {
		return;
	}
	await new Promise<void>((resolve) => {
		window.addEventListener('load', resolve.bind(null, void 1), {once: true});
	});
};

/**
 * Used to check mouse state outside mouse event.
 **/
export const mouse = (
	() => {
		const mouse = {
			hasButtonPressed: false,
			currentPressedButton: <number | null>null
		};
		document.documentElement.addEventListener('pointerdown', (event) => {
			mouse.hasButtonPressed = true;
			mouse.currentPressedButton = event.button;
		}, {capture: true});
		document.documentElement.addEventListener('pointerup', () => {
			mouse.hasButtonPressed = false;
			mouse.currentPressedButton = 0;
		}, {capture: true});
		// let mouseButtonWasPreviouslyPressed = false;
		// setInterval(() => {
		// 	if (mouse.hasButtonPressed !== mouseButtonWasPreviouslyPressed) {
		// 		if (mouse.hasButtonPressed) {
		// 			console.log(`Mouse button<${ mouse.currentPressedButton }> is pressed.`);
		// 		} else {
		// 			console.log('No mouse button currently pressed.');
		// 		}
		// 		mouseButtonWasPreviouslyPressed = mouse.hasButtonPressed;
		// 	}
		// }, 25);
		return mouse;
	}
)();

export function forceLayoutRecompute(element: HTMLElement) {
	element.offsetHeight; // eslint-disable-line no-unused-expressions
};
